(() => {
  const setHoverCapability = (enabled) => {
    document.documentElement?.toggleAttribute?.('data-theia-hover-capable', enabled);
  };

  const pointerSupportsHover = (event) => event.pointerType === 'mouse' || event.pointerType === 'pen';

  const syncHoverCapability = (event) => {
    if (pointerSupportsHover(event)) setHoverCapability(true);
    else if (event.pointerType === 'touch') setHoverCapability(false);
  };

  const productPreviewCards = (group) => [...group.querySelectorAll(':scope > *')];

  const productPreviewCardFromTarget = (group, target) => {
    let card = target;
    while (card && card.parentElement !== group) card = card.parentElement;
    if (card?.parentElement !== group || !card.querySelector?.('[data-theia-product-preview]')) return null;
    return card;
  };

  const clearProductPreviewPointerState = (group) => {
    group.removeAttribute('data-theia-product-pointer-active');
    productPreviewCards(group).forEach((card) => card.removeAttribute('data-theia-product-pointer-card'));
  };

  const onProductPreviewPointerOver = (event) => {
    const group = event.target?.closest?.('.theia-product-preview-group');
    if (!group) return;
    const card = productPreviewCardFromTarget(group, event.target);
    if (!card) return;
    productPreviewCards(group).forEach((item) => item.removeAttribute('data-theia-product-pointer-card'));
    group.setAttribute('data-theia-product-pointer-active', '');
    card.setAttribute('data-theia-product-pointer-card', '');
  };

  const onTheiaPointerOver = (event) => {
    syncHoverCapability(event);
    onProductPreviewPointerOver(event);
  };

  const onProductPreviewPointerOut = (event) => {
    const group = event.target?.closest?.('.theia-product-preview-group');
    if (!group || productPreviewCardFromTarget(group, event.relatedTarget)) return;
    clearProductPreviewPointerState(group);
  };

  class TheiaProductPreviewGallery {
    constructor(root) {
      this.root = root;
      this.slides = [...root.querySelectorAll('[data-theia-product-preview-slide]')];
      this.root.classList.toggle('has-secondary-image', this.slides.length > 1);
    }
  }

  class TheiaCollectionFilter {
    constructor(details) {
      this.details = details;
      this.summary = details.querySelector('summary');
      this.closeButton = details.querySelector('[data-theia-filter-close]');
      this.curtain = details.querySelector('[data-theia-filter-curtain]');
      this.locked = false;
      this.closing = false;
      this.onToggle = () => this.sync();
      this.onClose = () => this.close();
      this.onSummaryClick = (event) => {
        if (!this.details.open) return;
        event.preventDefault?.();
        this.close();
      };
      this.onKey = (event) => {
        if (event.key !== 'Escape' || !this.details.open) return;
        event.preventDefault?.();
        this.close();
      };
      details.addEventListener('toggle', this.onToggle);
      this.summary?.addEventListener('click', this.onSummaryClick);
      this.closeButton?.addEventListener('click', this.onClose);
      this.curtain?.addEventListener('click', this.onClose);
    }

    sync() {
      const open = Boolean(this.details.open);
      if (open === this.locked) return;
      this.locked = open;
      document.documentElement?.classList?.toggle('theia-filter-open', open);
      document.body?.classList?.toggle('theia-filter-open', open);
      document.dispatchEvent(new CustomEvent('theia:page-scroll-lock', { detail: { locked: open } }));
      if (open) {
        document.addEventListener('keydown', this.onKey);
        this.closeButton?.focus();
      } else {
        document.removeEventListener('keydown', this.onKey);
      }
    }

    close() {
      if (!this.details.open || this.closing) return;
      this.closing = true;
      this.details.classList.add('is-closing');
      window.setTimeout(() => {
        this.details.open = false;
        this.details.classList.remove('is-closing');
        this.closing = false;
        this.sync();
        this.summary?.focus();
      }, 320);
    }
  }

  class TheiaProduct {
    constructor(root) {
      this.root = root;
      this.slides = [...root.querySelectorAll('[data-theia-product-slide]')];
      this.thumbnails = [...root.querySelectorAll('[data-theia-product-thumbnail]')];
      this.thumbnailViewport = root.querySelector('[data-theia-product-thumbnails]');
      this.index = 0;
      root.querySelector('[data-theia-gallery-previous]')?.addEventListener('click', () => this.show(this.index - 1, -1));
      root.querySelector('[data-theia-gallery-next]')?.addEventListener('click', () => this.show(this.index + 1, 1));
      this.thumbnails.forEach((thumbnail) => thumbnail.addEventListener('click', () => this.show(Number(thumbnail.dataset.theiaProductThumbnail))));
      this.options = [...root.querySelectorAll('[data-theia-option]')];
      try { this.variants = JSON.parse(root.querySelector('[data-theia-product-variants]')?.textContent || '[]'); } catch { this.variants = []; }
      this.options.forEach((option) => option.addEventListener('change', () => this.updateVariant()));
      root.querySelectorAll('[data-theia-product-tab]').forEach((tab) => tab.addEventListener('click', () => this.showTab(tab.dataset.theiaProductTab)));
      this.remember();
    }
    show(next, direction = Math.sign(next - this.index)) {
      if (!this.slides.length) return;
      const previousIndex = this.index;
      this.index = (next + this.slides.length) % this.slides.length;
      this.slides.forEach((slide) => slide.getAnimations?.().forEach((animation) => animation.cancel()));
      this.slides.forEach((slide, index) => { slide.hidden = index !== this.index; slide.classList.toggle('is-active', index === this.index); });
      const activeSlide = this.slides[this.index];
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (this.index !== previousIndex && direction && !reduceMotion && activeSlide?.animate) {
        activeSlide.animate(
          [
            { opacity: 0, transform: `translate3d(${direction * 6}px, 0, 0)` },
            { opacity: 1, transform: 'translate3d(0, 0, 0)' },
          ],
          { duration: 240, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' },
        );
      }
      this.thumbnails.forEach((thumbnail) => {
        const isActive = Number(thumbnail.dataset.theiaProductThumbnail) === this.index;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-current', String(isActive));
      });
      const pageStart = Math.floor(this.index / 2) * 2;
      const target = this.thumbnails[pageStart];
      if (target && this.thumbnails[0]) {
        this.thumbnailViewport?.scrollTo({
          left: target.offsetLeft - this.thumbnails[0].offsetLeft,
          behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth',
        });
      }
    }
    updateVariant() {
      const values = this.options.map(({ value }) => value);
      const variant = this.variants.find((item) => item.options.every((value, index) => value === values[index]));
      if (!variant) return;
      const id = this.root.querySelector('[data-theia-variant-id]');
      if (id) id.value = variant.id;
      const price = this.root.querySelector('[data-theia-product-price]');
      if (price && window.Shopify?.formatMoney) price.textContent = window.Shopify.formatMoney(variant.price);
      const priceGroup = this.root.querySelector('[data-theia-product-price-group]');
      const comparePrice = this.root.querySelector('[data-theia-product-compare-price]');
      const saleBadge = this.root.querySelector('[data-theia-product-sale-badge]');
      const originalPriceRow = this.root.querySelector('[data-theia-product-original-price-row]');
      const compareAtPrice = Number(variant.compare_at_price || 0);
      const currentPrice = Number(variant.price || 0);
      let originalPrice = compareAtPrice;
      let salePercent = 0;
      let isOnSale = originalPrice > currentPrice;
      if (isOnSale) {
        salePercent = Math.round(((originalPrice - currentPrice) * 100) / originalPrice);
      } else if (priceGroup?.dataset.fallbackSaleHandles?.includes(this.root.dataset.productHandle || '')) {
        const multiplier = Number(priceGroup.dataset.fallbackOriginalPriceMultiplier || 125);
        const fallbackPercent = Number(priceGroup.dataset.fallbackSalePercent || 20);
        originalPrice = Math.round((currentPrice * multiplier) / 100);
        isOnSale = originalPrice > currentPrice;
        salePercent = fallbackPercent;
      }
      if (originalPriceRow) originalPriceRow.hidden = !isOnSale;
      if (comparePrice) {
        comparePrice.hidden = !isOnSale;
        if (isOnSale && window.Shopify?.formatMoney) comparePrice.textContent = window.Shopify.formatMoney(originalPrice);
      }
      if (saleBadge) {
        saleBadge.hidden = !isOnSale;
        if (isOnSale) {
          saleBadge.textContent = (this.root.dataset.salePercentOffTemplate || '[percent]% OFF').replace('[percent]', String(salePercent));
        }
      }
      const button = this.root.querySelector('[data-theia-add-to-cart]');
      if (button) {
        button.disabled = !variant.available;
        const label = button.querySelector('[data-theia-button-label]');
        if (label) label.textContent = variant.available ? this.root.dataset.addLabel : this.root.dataset.soldOutLabel;
      }
    }
    showTab(name) {
      this.root.querySelectorAll('[data-theia-product-tab]').forEach((tab) => {
        const isActive = tab.dataset.theiaProductTab === name;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
      });
      this.root.querySelectorAll('[data-theia-tab-panel]').forEach((panel) => {
        const isActive = panel.dataset.theiaTabPanel === name;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });
    }
    remember() {
      const handle = this.root.dataset.productHandle;
      if (!handle) return;
      try {
        const key = 'theia-recently-viewed';
        const history = JSON.parse(localStorage.getItem(key) || '[]').filter((item) => item !== handle);
        localStorage.setItem(key, JSON.stringify([handle, ...history].slice(0, 12)));
      } catch { /* Privacy mode keeps the page functional without history. */ }
    }
  }

  class TheiaRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.loaded === 'true') return;
      fetch(this.dataset.url).then((response) => response.text()).then((html) => {
        const documentHtml = new DOMParser().parseFromString(html, 'text/html');
        const fresh = documentHtml.querySelector('theia-recommendations');
        if (fresh?.innerHTML.trim()) {
          this.innerHTML = fresh.innerHTML;
          this.closest('[data-theia-product-rail]')?.dispatchEvent(new Event('theia:product-rail-updated'));
        }
        this.dataset.loaded = 'true';
      }).catch(() => {});
    }
  }

  class TheiaProductRail {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('.theia-product-rail__viewport');
      this.list = this.viewport?.querySelector?.('.theia-product-rail__items:not([hidden])') || root.querySelector('.theia-product-rail__items');
      this.controls = root.querySelector('[data-theia-rail-controls]');
      this.previous = root.querySelector('[data-theia-rail-previous]');
      this.next = root.querySelector('[data-theia-rail-next]');
      if (!this.viewport || !this.list || !this.controls || !this.previous || !this.next) return;
      this.previous.addEventListener('click', () => this.scroll(-1));
      this.next.addEventListener('click', () => this.scroll(1));
      this.viewport.addEventListener('scroll', () => this.update(), { passive: true });
      root.addEventListener('theia:product-rail-updated', () => {
        this.viewport.scrollLeft = 0;
        this.update();
      });
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', () => this.update());
      }
      this.update();
    }
    scroll(direction) {
      this.list = this.viewport.querySelector?.('.theia-product-rail__items:not([hidden])') || this.list;
      const cards = this.list.children;
      const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : this.viewport.clientWidth;
      this.viewport.scrollBy({ left: direction * step, behavior: 'smooth' });
    }
    update() {
      this.list = this.viewport.querySelector?.('.theia-product-rail__items:not([hidden])') || this.list;
      const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      this.controls.hidden = maximum <= 1;
      this.previous.disabled = this.viewport.scrollLeft <= 1;
      this.next.disabled = this.viewport.scrollLeft >= maximum - 1;
    }
  }

  class TheiaRecentlyViewed {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('.theia-product-rail__viewport');
      this.list = root.querySelector('[data-theia-recent-items]');
      this.suggestedList = root.querySelector('[data-theia-suggested-items]');
      this.template = root.querySelector('[data-theia-recent-card]');
      this.recentHeading = root.querySelector('[data-theia-recent-heading]');
      this.alternateHeading = root.querySelector('[data-theia-alternate-heading]');
      this.userSelected = false;
      this.recentHeading?.addEventListener('click', () => { this.userSelected = true; this.activate('recent', true); });
      this.alternateHeading?.addEventListener('click', () => { this.userSelected = true; this.activate('suggested', true); });
      this.activate('suggested');
      this.load();
    }
    activate(name, withMotion = false) {
      if (!this.list || !this.suggestedList) return;
      const recent = name === 'recent';
      const activeList = recent ? this.list : this.suggestedList;
      const switching = activeList.hidden;
      if (switching) {
        this.viewport?.classList.add('is-switching');
        this.switchAnimation?.cancel();
        this.switchAnimation = null;
      }
      this.list.hidden = !recent;
      this.suggestedList.hidden = recent;
      this.recentHeading?.setAttribute('aria-pressed', String(recent));
      this.alternateHeading?.setAttribute('aria-pressed', String(!recent));
      this.root.dispatchEvent(new Event('theia:product-rail-updated'));
      if (withMotion && switching && !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
        const animation = activeList.animate?.(
          [{ opacity: 0, transform: 'translateX(12px)' }, { opacity: 1, transform: 'translateX(0)' }],
          { duration: 320, easing: 'cubic-bezier(0.4, 0, 0.6, 1)' }
        );
        this.switchAnimation = animation || null;
        const finish = () => {
          if (this.switchAnimation !== animation) return;
          this.switchAnimation = null;
          this.viewport?.classList.remove('is-switching');
        };
        animation?.finished?.then(
          finish,
          finish
        );
        if (!animation) this.viewport?.classList.remove('is-switching');
      } else if (switching) {
        this.viewport?.classList.remove('is-switching');
      }
    }
    async load() {
      if (!this.list || !this.template || window.Shopify?.designMode) return;
      let handles = [];
      try { handles = JSON.parse(localStorage.getItem('theia-recently-viewed') || '[]'); } catch { return; }
      handles = handles.filter((handle) => handle !== this.root.dataset.currentHandle).slice(0, Number(this.root.dataset.limit || 6));
      const localeRoot = window.Shopify?.routes?.root || (this.root.dataset.locale === 'en' ? '/en/' : '/');
      const products = (await Promise.all(handles.map((handle) => fetch(`${localeRoot}products/${encodeURIComponent(handle)}?section_id=theia-main-product`)
        .then((response) => response.ok ? response.text() : '')
        .then((html) => {
          const documentHtml = new DOMParser().parseFromString(html, 'text/html');
          const data = documentHtml.querySelector('[data-theia-product-card-data]')?.textContent;
          return data ? JSON.parse(data) : null;
        })
        .catch(() => null)))).filter(Boolean);
      if (!products.length) return;
      this.list.innerHTML = '';
      if (this.recentHeading) this.recentHeading.disabled = false;
      products.forEach((cardData) => {
        const card = this.template.content.firstElementChild.cloneNode(true);
        const links = card.querySelectorAll('a');
        const link = links[0];
        const metaLink = links[1] || link;
        const image = card.querySelector('img');
        const galleryLink = card.querySelector('.theia-product-preview-gallery__link') || link;
        const gallery = card.querySelector('[data-theia-product-preview-gallery]');
        const firstSlide = card.querySelector('[data-theia-product-preview-slide]');
        const images = Array.isArray(cardData.images) && cardData.images.length
          ? cardData.images
          : [{ src: cardData.image || '', alt: cardData.imageAlt || cardData.englishTitle || cardData.tcTitle || '' }];
        const cardHref = cardData.url;
        link.href = cardHref;
        if (metaLink) metaLink.href = cardHref;
        if (galleryLink) galleryLink.href = cardHref;
        if (image) {
          image.src = images[0]?.src || '';
          image.alt = images[0]?.alt || cardData.englishTitle || cardData.tcTitle || '';
        }
        if (galleryLink && firstSlide) {
          images.slice(1, 2).forEach((entry) => {
            const slide = firstSlide.cloneNode(true);
            const slideImage = slide.querySelector('img');
            slide.classList.remove('is-active');
            slide.setAttribute('aria-hidden', 'true');
            if (slideImage) {
              slideImage.src = entry.src || '';
              slideImage.alt = entry.alt || cardData.englishTitle || cardData.tcTitle || '';
            }
            galleryLink.append(slide);
          });
        }
        card.querySelector('[data-theia-product-vendor]').textContent = cardData.vendor || '';
        card.querySelector('[data-theia-product-title]').textContent = this.root.dataset.locale === 'zh-TW' ? (cardData.tcTitle || '') : (cardData.englishTitle || '');
        const englishTitle = card.querySelector('[data-theia-english-product-name]');
        if (englishTitle) englishTitle.textContent = cardData.englishTitle || '';
        card.querySelector('.theia-product-rail__price').textContent = window.Shopify?.formatMoney ? window.Shopify.formatMoney(cardData.price) : `${(cardData.price / 100).toFixed(2)}`;
        this.list.append(card);
        if (gallery) new TheiaProductPreviewGallery(gallery);
      });
      [...this.suggestedList.children].forEach((card, index) => { card.hidden = index >= products.length; });
      if (!this.userSelected) this.activate('recent');
      else this.root.dispatchEvent(new Event('theia:product-rail-updated'));
    }
  }

  window.TheiaProductPreviewGallery = TheiaProductPreviewGallery;
  window.TheiaProduct = TheiaProduct;
  window.TheiaCollectionFilter = TheiaCollectionFilter;
  window.TheiaRecommendations = TheiaRecommendations;
  window.TheiaProductRail = TheiaProductRail;
  window.TheiaRecentlyViewed = TheiaRecentlyViewed;
  if (!customElements.get('theia-recommendations')) customElements.define('theia-recommendations', TheiaRecommendations);

  if (globalThis.matchMedia?.('(any-hover: hover) and (any-pointer: fine)')?.matches) setHoverCapability(true);
  document.addEventListener('pointerover', onTheiaPointerOver);
  document.addEventListener('pointerdown', syncHoverCapability);
  document.addEventListener('pointerout', onProductPreviewPointerOut);

  const mount = (scope = document) => {
    scope.querySelectorAll('[data-theia-product-rail]:not([data-theia-rail-mounted])').forEach((root) => {
      root.dataset.theiaRailMounted = 'true';
      new TheiaProductRail(root);
    });
    scope.querySelectorAll('[data-theia-filter-details]:not([data-theia-mounted])').forEach((details) => {
      details.dataset.theiaMounted = 'true';
      new TheiaCollectionFilter(details);
    });
    scope.querySelectorAll('[data-theia-product-preview-gallery]:not([data-theia-mounted])').forEach((root) => {
      root.dataset.theiaMounted = 'true';
      new TheiaProductPreviewGallery(root);
    });
    scope.querySelectorAll('[data-theia-product]:not([data-theia-mounted])').forEach((root) => { root.dataset.theiaMounted = 'true'; new TheiaProduct(root); });
    scope.querySelectorAll('[data-theia-recently-viewed]:not([data-theia-mounted])').forEach((root) => { root.dataset.theiaMounted = 'true'; new TheiaRecentlyViewed(root); });
    scope.querySelectorAll('[data-theia-sort]').forEach((sort) => sort.addEventListener('change', () => sort.form?.submit(), { once: true }));
  };
  document.addEventListener('DOMContentLoaded', () => mount());
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
